import { useEffect, useState } from "react";
import { Link as RouterLink, Navigate, RouteObject, useParams } from "react-router-dom";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import { Skeleton, Button, CircularProgress, Stack, IconButton, Tooltip, Box } from "@mui/material";
import useDomains from "cohorts/hooks/useDomains";
import CollaboratorsModal from "datasets/components/CollaboratorsModal";
import { DatasetAssistantPanel } from "datasets/components/assistant/DatasetAssistantPanel";
import {
  loadDatasetAssistantState,
  updateDatasetAssistantState,
} from "datasets/components/assistant/datasetAssistantState";
import useDatasetAssistant from "datasets/components/assistant/hooks/useDatasetAssistant";
import useDatasetAssistantV2 from "datasets/components/assistant/hooks/useDatasetAssistantV2";
import useDataset from "datasets/hooks/useDataset";
import useIsMasterDataset from "datasets/hooks/useIsMasterDataset";
import DatasetReport from "datasets/pages/dashboards/pages/DatasetReport";
import DatasetReports from "datasets/pages/dashboards/pages/DatasetReports";
import DatasetFiles from "datasets/pages/files/DatasetFiles";
import DatasetOverview from "datasets/pages/overview/DatasetOverview";
import DatasetSettings from "datasets/pages/settings/DatasetSettings";
import DatasetTables from "datasets/pages/tables/DatasetTables";
import { DatasetAccessLevel } from "datasets/types/enums/access-level";
import yn from "yn";
import { CohortIcon, SparklesIcon } from "~/assets/icons";
import { HEADER_HEIGHT, TitleBreadcrumbs } from "~/components/AppHeader";
import RouteTabs, { ROUTE_TABS_HEIGHT, RouteTab } from "~/components/RouteTabs";
import useAppHeader from "~/contexts/header";
import { useDocumentTitle } from "~/hooks/useDocumentTitle";
import { useSafeRoutes } from "~/hooks/useSafeRoutes";
import useUserPermission from "~/hooks/useUserPermission";
import { ROUTE_404 } from "~/router/constants";
import { trackEvent } from "~/utils/monitoring";

const {
  VITE_DATASETS_ENABLED,
  VITE_COHORT_EXPLORER_ENABLED,
  VITE_DATASET_FILES_ENABLED,
  VITE_DATASET_PERMISSIONS_ENABLED,
} = import.meta.env;

const routeDefinitions: RouteObject[] = [
  { index: true, element: <Navigate to="overview" replace /> },
  { path: "overview", element: <DatasetOverview /> },
  {
    path: "reports",
    element: <DatasetReports />,
  },
  {
    path: "analysis",
    element: <Navigate to="../overview" replace />,
  },
  {
    path: "analysis-v2",
    element: <Navigate to="../overview" replace />,
  },
  {
    path: "reports/:reportId",
    element: <DatasetReport />,
  },
  {
    path: "tables/*",
    element: <DatasetTables />,
  },
  {
    path: "files/:prefixId?/*",
    element: <DatasetFiles />,
  },

  {
    path: "settings/*",
    element: <DatasetSettings />,
  },
];

const DatasetDetails = () => {
  const { datasetId, "*": subRoute } = useParams<{ datasetId: string; "*": string }>();
  const routes = useSafeRoutes(routeDefinitions);
  const { data: dataset, isLoading, error } = useDataset(datasetId);
  const isMasterDataset = useIsMasterDataset();
  const hasEditPermission = useUserPermission("edit:catalog-dataset");
  const showSettings = yn(VITE_DATASET_PERMISSIONS_ENABLED)
    ? dataset?.access_level === DatasetAccessLevel.OWNER
    : hasEditPermission;
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [isAssistantCollapsed, setIsAssistantCollapsed] = useState(
    () => loadDatasetAssistantState().collapsed
  );
  const toggleAssistantCollapsed = () => {
    setIsAssistantCollapsed((prev) => {
      const nextCollapsed = !prev;
      updateDatasetAssistantState({ collapsed: nextCollapsed });
      return nextCollapsed;
    });
  };
  const isPreviewUser = yn(VITE_DATASET_PERMISSIONS_ENABLED)
    ? dataset?.access_level === DatasetAccessLevel.PREVIEW
    : false;

  const aiAgentsEnabled = dataset?.aiAgentsEnabled === true;
  const showAnalysisV1 = useDatasetAssistant() && !isPreviewUser && aiAgentsEnabled;
  const showAnalysisV2 = useDatasetAssistantV2() && !isPreviewUser && aiAgentsEnabled;
  const { data: domains } = useDomains();
  const canViewCohortExplorer = useUserPermission("view:cohort-explorer-app");
  const cohortExplorerEnabled =
    canViewCohortExplorer && yn(VITE_COHORT_EXPLORER_ENABLED) && !!domains?.length;

  const currentTab = subRoute?.split("/")[0] || "overview";
  const isReportEmbed = currentTab === "reports" && subRoute?.split("/")[1];

  useDocumentTitle(dataset?.title);
  const { setTitle } = useAppHeader();

  // Track dataset views only if dataset or tab has changed
  useEffect(() => {
    if (dataset?.id && currentTab) {
      trackEvent("dataset_viewed", {
        dataset_id: dataset.id,
        page: currentTab === "reports" ? "dashboards" : currentTab,
      });
    }
  }, [dataset?.id, currentTab]);

  useEffect(() => {
    setTitle(
      <TitleBreadcrumbs
        crumbs={[
          { title: "Data Browser", href: "/data-browser" },
          { title: "Datasets", href: "/data-browser/datasets" },
          { title: isLoading ? <Skeleton width={75} /> : dataset?.title, href: location.pathname },
        ]}
      />
    );
  }, [dataset, isLoading, setTitle]);

  // Sanitize tab value to prevent MUI TabContext errors
  const getValidTabValue = () => {
    if (currentTab === "settings") {
      return null;
    }
    if (isPreviewUser) {
      const allowedTabs = ["overview", "reports"];
      if (!allowedTabs.includes(currentTab)) {
        return "overview";
      }
    }
    return currentTab;
  };

  if (!yn(VITE_DATASETS_ENABLED) || error?.status === 404) {
    return <Navigate to={ROUTE_404} replace />;
  }

  if (!showSettings && currentTab === "settings" && !isLoading) {
    return <Navigate to="/404" replace />;
  }

  // Redirect preview users trying to access restricted routes
  if (isPreviewUser && !isLoading) {
    const restrictedRoutes = ["analysis", "analysis-v2", "tables", "files", "settings"];
    if (restrictedRoutes.includes(currentTab)) {
      return <Navigate to="overview" replace />;
    }
  }

  // Build tabs based on permissions and feature flags
  const tabs: RouteTab[] = [
    { label: "Overview", value: "overview" },
    { label: "Dashboards", value: "reports" },
    {
      label: "Tables",
      value: "tables",
      visible: !isPreviewUser,
    },
    {
      label: "Files",
      value: "files",
      visible: yn(VITE_DATASET_FILES_ENABLED) && !isMasterDataset && !isPreviewUser,
    },
  ];

  // Action buttons for the tab bar
  const renderActions = () => {
    const isDatasetPermissionsEnabled = yn(VITE_DATASET_PERMISSIONS_ENABLED);
    const accessLevel = dataset?.access_level;
    const showShare = isDatasetPermissionsEnabled && accessLevel === DatasetAccessLevel.OWNER;
    const showAssistant = showAnalysisV1 || showAnalysisV2;
    const showExpandAssistant = showAssistant && isAssistantCollapsed;
    const showCohort = cohortExplorerEnabled && !!dataset;
    if (!showSettings && !showShare && !showExpandAssistant && !showCohort) {
      return null;
    }

    return (
      <Stack direction="row" spacing={1} alignItems="center">
        {showCohort && (
          <Button
            component={RouterLink}
            to="/cohorts/new"
            state={{ datasetId: dataset.id }}
            size="small"
            startIcon={
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 22,
                  height: 22,
                  borderRadius: "50%",
                  bgcolor: "primary.main",
                  color: "common.white",
                  "& > *": { width: 15, height: 15 },
                }}
              >
                <CohortIcon />
              </Box>
            }
            sx={{
              backgroundColor: "grey.200",
              color: "grey.800",
              textTransform: "none",
              fontWeight: 500,
              fontSize: 13.5,
              height: 32,
              px: 1.5,
              "&:hover": { backgroundColor: "grey.300", color: "grey.900" },
            }}
          >
            Build a Cohort
          </Button>
        )}
        {showExpandAssistant && (
          <Tooltip arrow enterDelay={300} title="Show Analysis Agent">
            <Button
              data-testid="expand-assistant-button"
              size="small"
              onClick={toggleAssistantCollapsed}
              startIcon={
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 20,
                    height: 20,
                    borderRadius: "50%",
                    bgcolor: "primary.main",
                    color: "common.white",
                    "& > *": { width: 12, height: 12 },
                  }}
                >
                  <SparklesIcon />
                </Box>
              }
              sx={{
                backgroundColor: "grey.200",
                color: "grey.800",
                textTransform: "none",
                fontWeight: 500,
                fontSize: 13.5,
                height: 32,
                px: 1.5,
                "&:hover": {
                  backgroundColor: "grey.300",
                  color: "grey.900",
                },
              }}
            >
              Analyze
            </Button>
          </Tooltip>
        )}
        {showShare && (
          <Button
            data-testid="share-dataset-button"
            variant="contained"
            onClick={() => setIsShareModalOpen(true)}
            sx={{
              backgroundColor: "grey.200",
              color: "grey.800",
              textTransform: "none",
              fontWeight: 500,
              fontSize: 14,
              height: 32,
              px: 1.5,
              "&:hover": { backgroundColor: "grey.300", color: "grey.900" },
            }}
          >
            Share
          </Button>
        )}
        {showSettings && (
          <Tooltip arrow enterDelay={300} title="Dataset Settings">
            <IconButton
              data-testid="dataset-settings-button"
              component={currentTab === "settings" ? "button" : RouterLink}
              to={
                currentTab === "settings"
                  ? undefined
                  : `/data-browser/datasets/${datasetId}/settings`
              }
              size="small"
              sx={{
                backgroundColor: currentTab === "settings" ? "grey.900" : "grey.200",
                color: currentTab === "settings" ? "white" : "grey.800",
                height: 32,
                width: 32,
                "&:hover": {
                  backgroundColor: currentTab === "settings" ? "grey.900" : "grey.300",
                  color: currentTab === "settings" ? "white" : "grey.900",
                },
              }}
            >
              <SettingsOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Stack>
    );
  };

  // Loading state
  const loadingContent = (
    <Stack
      height="100%"
      minHeight={`calc(100vh - ${HEADER_HEIGHT} - ${ROUTE_TABS_HEIGHT}px)`}
      flex={1}
      alignItems="center"
      justifyContent="center"
      bgcolor="common.pageBackground"
    >
      <CircularProgress sx={{ color: "grey.400" }} />
    </Stack>
  );

  return (
    <>
      <Box sx={{ display: "flex", flex: 1, minHeight: 0 }}>
        <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
          <RouteTabs
            tabs={tabs}
            currentTab={getValidTabValue()}
            actions={renderActions()}
            showTabs={!isReportEmbed}
          >
            {isLoading ? loadingContent : routes}
          </RouteTabs>
        </Box>

        {(showAnalysisV1 || showAnalysisV2) && datasetId && (
          <DatasetAssistantPanel
            datasetId={datasetId}
            isCollapsed={isAssistantCollapsed}
            onCollapse={toggleAssistantCollapsed}
            showV1={showAnalysisV1}
            showV2={showAnalysisV2}
          />
        )}
      </Box>

      {isShareModalOpen && dataset && (
        <CollaboratorsModal
          open={isShareModalOpen}
          onClose={() => setIsShareModalOpen(false)}
          dataset={dataset}
          datasetId={datasetId}
        />
      )}
    </>
  );
};

export default DatasetDetails;
